
import Konva                     from 'konva';
import { LineCap }               from 'konva/lib/Shape';

import { VirtualCanvas }         from '../virtual.canvas';
import { Logger }                from '@/util/logger';
import { Visual }                from '../visuals/visual';
import { KonvaObjectFactory }    from './konva.object.factory';
import { Rectangle }             from '../visuals/rectangle';
import { Text }                  from '../visuals/text';
import { Circle }                from '../visuals/circle';
import { Line }                  from '../visuals/line';
import { EventHandlerInterface } from '../events/event.handler.interface';
import {KeyboardListenerInterface} from '../keyboard.listener.interface';

/**
 *
 * @class KonvaCanvas
 *
 * Adaptor for working with a Konva canvas
 *
 *   https://konvajs.org
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2024
 *
 */
export class KonvaCanvas extends VirtualCanvas {

  /** the virtual canvas we are using  for drawing */
  private readonly stage: Konva.Stage|null                = null;

  /** the base/common layer for drawing */
  private readonly baseLayer: Konva.Layer|null            = null;

  /** the hosting DOM element (ID) */
  public container: string                                = '';

  /** keyboard listener...if there is one */
  public keyListener: KeyboardListenerInterface|null      = null;

  /** @brief standard constructor */
  constructor(init?: Partial<KonvaCanvas>) {

    super();

    Logger.debug(`KonvaCanvas ctor() building new canvas...`);

    Object.assign(this, init);

    this.stage = new Konva.Stage({
      container: this.container,
      width:     this.width,
      height:    this.height,
    });

    /* we need at least one layer to draw on ... */

    this.baseLayer = new Konva.Layer();

    this.stage.add(this.baseLayer);

    /* enable keyboard events from the stage */

    let outer: HTMLDivElement = this.stage.container();

    outer.tabIndex = 1;
    outer.focus();

    outer.addEventListener('keydown', (event: Event): void => {

      if (this.keyListener) {

        if (event instanceof KeyboardEvent) {


          this.keyListener.onKey(event as KeyboardEvent);
        }
      }

    });
  }

  /** helper to give keyboard focus to the canvas */
  public focus(): void {

    let outer: HTMLDivElement|null = this.stage?.container() ?? null;

    if (outer) {

      outer.tabIndex = 1;
      outer.focus();
    }
  }

  /** resize the canvas to exactly this width and height */
  public resize(width: number, height: number): VirtualCanvas {

    if (!this.stage) {

      /* no actual canvas yet */

      return this;
    }

    this.width  = width;
    this.height = height;

    this.stage.width(this.width);
    this.stage.height(this.height);

    this.onResize();

    return this;
  }

  /** helper to clear any rendered visuals */
  public clear(): VirtualCanvas {

    Logger.debug(`KonvaCanvas clear() ...`);

    if (!this.baseLayer) {

      return this;
    }

    /* remove AND destroy all visuals */

    this.baseLayer.destroyChildren();

    return this;
  }

  /** add a visual to the canvas and return an ID/handle for that visual. */
  public addShape(obj: Visual, eventHandler: EventHandlerInterface|null|undefined = null): any {

    let handle: Konva.Shape|null = KonvaObjectFactory.build(obj, eventHandler);

    if (!handle) {

      Logger.error(`KonvaCanvas add() - could not create visual: `, obj);

      return null;
    }

    this.baseLayer?.add(handle);

    return handle;
  }

  /** update the canvas object with the given visual parameters, and return the existing ID/handle */
  public updateShape(obj: Visual): any {

    if (!obj.handle) {

      /* there is no canvas shape yet */

      return null;
    }

    if (obj.handle instanceof Konva.Rect) {

      /* update for a rectangle */

      let options: Rectangle = obj as Rectangle;

      (obj.handle as Konva.Shape).setAttrs({
        x:            options.x               ? options.x               : 0,
        y:            options.y               ? options.y               : 0,
        width:        options.width           ? options.width           : 10,
        height:       options.height          ? options.height          : 10,
        cornerRadius: options.cornerRadius    ? options.cornerRadius    : 0,
        fill:         options.backgroundColor ? options.backgroundColor : 'green',
        stroke:       options.borderColor     ? options.borderColor     : 'red',
        strokeWidth:  options.borderWidth     ? options.borderWidth     : 2,
        visible:      options.visible         ? options.visible         : true,
        opacity:      options.opacity         ? options.opacity         : 1.0,
      });

    } else if (obj.handle instanceof Konva.Text) {

      /* update for a Text */

      let options: Text = obj as Text;

      (obj.handle as Konva.Shape).setAttrs({
        x:            options.x         ? options.x         : 0,
        y:            options.y         ? options.y         : 0,
        text:         options.text      ? options.text      : 'XO',
        fill:         options.color     ? options.color     : 'white',
        family:       options.family    ? options.family    : 'Arial',
        fontSize:     options.size      ? options.size      : 10,
        fontWeight:   options.weight    ? options.weight    : 'normal',
        fontVariant:  options.variation ? options.variation : '',
        fontStyle:    options.style     ? options.style     : '',
        visible:      options.visible   ? options.visible   : true,
        opacity:      options.opacity   ? options.opacity   : 1.0,
        rotation:     options.rotation  ? options.rotation  : 0.0
      });

    } else if (obj.handle instanceof Konva.Circle) {

      /* update for a Circle */

      let options: Circle = obj as Circle;

      (obj.handle as Konva.Shape).setAttrs({
        x:            options.x               ? options.x               : 0,
        y:            options.y               ? options.y               : 0,
        radius:       options.radius          ? options.radius          : 10,
        fill:         options.backgroundColor ? options.backgroundColor : 'white',
        stroke:       options.borderColor     ? options.borderColor     : 'black',
        strokeWidth:  options.borderWidth     ? options.borderWidth     : 2,
        visible:      options.visible         ? options.visible         : true,
        opacity:      options.opacity         ? options.opacity         : 1.0
      });

    } else if (obj.handle instanceof Konva.Line) {

      /* update for a Line */

      let options: Line = obj as Line;

      let points: number[] = [];

      if (options.points) {

        options.points.forEach((point: {x: number, y: number}): void => {

          points.push(point.x, point.y);

        });

      } else {

        if (options.x && options.y) {

          points.push(options.x, options.y, options.x, options.y)
        }

      }

      let lineCap: LineCap = 'square';

      if (options.lineCap && options.lineCap == 'round') {

        lineCap = 'round';
      }

      if (options.lineCap && options.lineCap == 'butt') {

        lineCap = 'butt';
      }

      (obj.handle as Konva.Shape).setAttrs({
        points,
        fill:         options.color     ? options.color     : 'white',
        stroke:       options.color     ? options.color     : 'black',
        strokeWidth:  options.lineWidth ? options.lineWidth : 2,
        visible:      options.visible   ? options.visible   : true,
        opacity:      options.opacity   ? options.opacity   : 1.0,
        lineCap
      });

    }

    /* we didn't match a known visual */

    return null;
  }


}
